// =============================================================================
// MeuDriver — Hook: useStudentProfile (Batch R3)
// =============================================================================
// Perfil avançado do estudante: license_goal, current_level, learning_notes,
// preferred_schedule. Usa RPC update_student_profile (SECURITY DEFINER).
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { ProfileRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StudentProfileDetails {
  license_goal: ProfileRow['license_goal']
  current_level: ProfileRow['current_level']
  learning_notes: ProfileRow['learning_notes']
  preferred_schedule: ProfileRow['preferred_schedule']
}

export interface UpdateStudentProfileArgs {
  license_goal?: string | null
  current_level?: string | null
  learning_notes?: string | null
  preferred_schedule?: string | null
}

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

export const studentProfileKeys = {
  details: (userId: string) => ['student_profile_details', userId] as const,
}

// ---------------------------------------------------------------------------
// useStudentProfileDetails
// ---------------------------------------------------------------------------

export function useStudentProfileDetails() {
  const { user } = useAuth()

  return useQuery<StudentProfileDetails>({
    queryKey: studentProfileKeys.details(user?.id ?? ''),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('license_goal, current_level, learning_notes, preferred_schedule')
        .eq('id', user!.id)
        .single()

      if (error) throw error

      return {
        license_goal: (data as StudentProfileDetails).license_goal ?? null,
        current_level: (data as StudentProfileDetails).current_level ?? null,
        learning_notes: (data as StudentProfileDetails).learning_notes ?? null,
        preferred_schedule: (data as StudentProfileDetails).preferred_schedule ?? null,
      }
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdateStudentProfile
// ---------------------------------------------------------------------------

export function useUpdateStudentProfile() {
  const { user, refreshProfile } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: UpdateStudentProfileArgs) => {
      const { error } = await supabase.rpc('update_student_profile', {
        p_license_goal:       args.license_goal ?? null,
        p_current_level:      args.current_level ?? null,
        p_learning_notes:     args.learning_notes ?? null,
        p_preferred_schedule: args.preferred_schedule ?? null,
      })
      if (error) throw error
    },
    onSuccess: async () => {
      if (user?.id) {
        await queryClient.invalidateQueries({
          queryKey: studentProfileKeys.details(user.id),
        })
        await refreshProfile()
      }
    },
  })
}
